import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Table, TableColumn } from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
  alertApiRef,
} from '@backstage/core-plugin-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStyles } from './Applications.styles';
import { TableSecretData } from '../table/TableSecretData';
import {
  getApigeeApps,
  deactivateApigeeApp,
  resetAppClientSecret,
} from '../../api/apigee';
import { DeleteSharp, RefreshSharp } from '@mui/icons-material';
import { colors } from '../../theme';

type Data = {
  id: string;
  name: string;
  displayName?: string;
  consumerKey: string;
  consumerSecret: string;
  resettable: boolean;
  removable: boolean;
};

type ActionColumnProps = {
  data: Data;
  isApiProject?: boolean;
};

function updateAppSecret(
  apps: ApigeeApp[],
  targetKey: string,
  newSecret: string,
) {
  return apps.map(app =>
    app.consumerKey === targetKey ? { ...app, consumerSecret: newSecret } : app,
  );
}

function removeApp(apps: ApigeeApp[], deletedId: string) {
  return apps.filter(app => app.id !== deletedId);
}

const ResetSecretButton = ({ data }: ActionColumnProps) => {
  const [open, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const identity = useApi(identityApiRef);
  const discovery = useApi(discoveryApiRef);
  const alert = useApi(alertApiRef);

  const resetClientMutation = useMutation({
    mutationFn: ({ appId, clientSecret }: ResetClientSecretRequest) =>
      resetAppClientSecret(discovery, identity, appId, clientSecret),
    onSuccess: result => {
      const appName = data.displayName ?? data.name;

      alert.post({
        message: `Client secret of '${appName}' has been successfully reset.`,
        severity: 'success',
        display: 'transient',
      });

      queryClient.setQueryData(['apigeeApps'], (apps: ApigeeApp[]) =>
        apps
          ? updateAppSecret(apps, result.consumerKey, result.consumerSecret)
          : [],
      );

      queryClient.setQueryData(
        ['apigeeCompanyApps'],
        (appsByCompany: ApigeeCompanyApps) =>
          appsByCompany
            ? Object.fromEntries(
                Object.entries(appsByCompany).map(([companyName, apps]) => {
                  const updatedApps = updateAppSecret(
                    apps,
                    result.consumerKey,
                    result.consumerSecret,
                  );

                  return [companyName, updatedApps];
                }),
              )
            : {},
      );
    },
    onSettled: () => setOpen(false),
  });

  return (
    <>
      <Tooltip title="Reset Client Secret" placement="top" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          disabled={!data.resettable}
          data-testid={`reset-client-secret-${data.id}`}
        >
          <RefreshSharp
            sx={{
              color: data.resettable ? colors.red : 'rgba(0, 0, 0, 0.3)',
            }}
          />
        </IconButton>
      </Tooltip>

      <Dialog
        keepMounted
        open={open}
        onClose={() => setOpen(false)}
        data-testid={`reset-client-secret-dialog-${data.id}`}
      >
        <DialogTitle>
          Reset Client Secret for {data.displayName || data.name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText fontSize={14}>
            This will permanently revoke your existing client secret and replace
            it with a new one. Applications using the current secret will stop
            working until updated. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{ height: 36 }}
            onClick={() => setOpen(false)}
            data-testid={`reset-client-secret-cancel-${data.id}`}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ height: 36 }}
            onClick={() =>
              resetClientMutation.mutate({
                appId: data.id,
                clientSecret: data.consumerSecret,
              })
            }
            data-testid={`reset-client-secret-confirm-${data.id}`}
          >
            {resetClientMutation.isPending ? (
              <CircularProgress sx={{ color: 'white' }} size={16} />
            ) : (
              'Yes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RemoveClientButton = ({ data, isApiProject }: ActionColumnProps) => {
  const [open, setOpen] = useState(false);

  const queryClient = useQueryClient();
  const identity = useApi(identityApiRef);
  const discovery = useApi(discoveryApiRef);
  const alert = useApi(alertApiRef);

  const removeAppMutation = useMutation({
    mutationFn: ({ appId, clientSecret }: ResetClientSecretRequest) =>
      deactivateApigeeApp(discovery, identity, appId, clientSecret),
    onSuccess: result => {
      const appName = data.displayName ?? data.name;

      alert.post({
        message: `Successfully removed '${appName}'.`,
        severity: 'success',
        display: 'transient',
      });

      queryClient.setQueryData(['apigeeApps'], (apps: ApigeeApp[]) =>
        apps ? removeApp(apps, result.id) : [],
      );

      queryClient.setQueryData(
        ['apigeeCompanyApps'],
        (appsByCompany: ApigeeCompanyApps) =>
          appsByCompany
            ? Object.fromEntries(
                Object.entries(appsByCompany).map(([companyName, apps]) => {
                  const updatedApps = removeApp(apps, result.id);

                  return [companyName, updatedApps];
                }),
              )
            : {},
      );
    },
    onSettled: () => setOpen(false),
  });

  const clientTitle = isApiProject ? 'API Project' : 'Sandbox Client';

  return (
    <>
      <Tooltip title={`Remove ${clientTitle}`} placement="top" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          disabled={!data.removable}
          data-testid={`remove-app-${data.id}`}
        >
          <DeleteSharp
            sx={{
              color: data.removable ? colors.red : 'rgba(0, 0, 0, 0.3)',
            }}
          />
        </IconButton>
      </Tooltip>

      <Dialog
        keepMounted
        open={open}
        onClose={() => setOpen(false)}
        data-testid={`remove-app-dialog-${data.id}`}
      >
        <DialogTitle>Remove {data.displayName || data.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText fontSize={14}>
            Removing this client will delete all associated credentials and
            configurations. This action is permanent and cannot be undone. Do
            you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{ height: 36 }}
            onClick={() => setOpen(false)}
            data-testid={`remove-app-cancel-${data.id}`}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ height: 36 }}
            onClick={() =>
              removeAppMutation.mutate({
                appId: data.id,
                clientSecret: data.consumerSecret,
              })
            }
            data-testid={`remove-app-confirm-${data.id}`}
          >
            {removeAppMutation.isPending ? (
              <CircularProgress sx={{ color: 'white' }} size={16} />
            ) : (
              'Yes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const ActionColumn = ({ data, isApiProject }: ActionColumnProps) => {
  return (
    <Stack direction="row" gap={0.5} pr={8}>
      <ResetSecretButton data={data} />
      <RemoveClientButton data={data} isApiProject={isApiProject} />
    </Stack>
  );
};

const columns: TableColumn[] = [
  {
    title: 'Client Name',
    field: 'name',
    highlight: true,
    render: data => (
      <Stack>
        <Typography variant="body2" fontWeight={700}>
          {(data as Data).displayName}
        </Typography>

        {(data as Data).displayName !== (data as Data).name && (
          <Typography variant="body2" fontSize={11}>
            {(data as Data).name}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    title: 'Client ID',
    field: 'consumerKey',
    render: data => <TableSecretData secret={(data as Data).consumerKey} />,
  },
  {
    title: 'Client Secret',
    field: 'consumerSecret',
    render: data => <TableSecretData secret={(data as Data).consumerSecret} />,
  },
  {
    title: 'Actions',
    width: 'auto',
    render: data => <ActionColumn data={data as Data} />,
  },
];

export const ApplicationsTable = () => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const identity = useApi(identityApiRef);

  const apigeeApps = useQuery({
    queryKey: ['apigeeApps'],
    queryFn: () => getApigeeApps(discovery, identity),
  });

  return (
    <div className={classes.tableContainer}>
      {apigeeApps.error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <Typography component="span" fontWeight={500}>
            Error!
          </Typography>{' '}
          Something went wrong with getting the sandbox clients.
        </Alert>
      )}

      <Table
        options={{ paging: false }}
        data={(apigeeApps.data ?? []).sort((a, b) =>
          a.name.localeCompare(b.name),
        )}
        isLoading={apigeeApps.isLoading}
        columns={columns}
        title="My Sandbox Clients"
        // @ts-ignore - subtitle can be an element but backstage expects string - https://github.com/backstage/backstage/blob/master/packages/core-components/src/components/Table/Table.tsx#L450
        subtitle={
          <Typography color="textSecondary" variant="body2">
            Sandbox clients are limited to the sandbox environment with a
            restricted dataset and API request quota. Use these clients to
            develop your ideas and explore our APIs. This allows you to get a
            feel for our API experience before you progress to a test
            environment.
          </Typography>
        }
      />
    </div>
  );
};
